import Component from '@glimmer/component';

const toneClasses = {
  accent: {
    common: 'ring-blue-500',
    solid: 'text-white bg-blue-500 hover:bg-blue-600',
    muted: 'text-blue-600 bg-blue-100 hover:bg-blue-200',
    transparent: 'text-blue-600 hover:text-blue-700 hover:underline',
  },
  positive: {
    common: 'ring-green-500',
    solid: 'text-white bg-green-500 hover:bg-green-600',
    muted: 'text-green-600 bg-green-100 hover:bg-green-200',
    transparent: 'text-green-600 hover:underline',
  },
  critical: {
    common: 'ring-red-500',
    solid: 'text-white bg-red-500 hover:bg-red-600',
    muted: 'text-red-600 bg-red-100 hover:bg-red-200',
    transparent: 'text-red-600 hover:underline',
  },
  disabled: {
    common: 'ring-grey-300',
    solid: 'text-white bg-grey-300',
    muted: 'text-grey-500 bg-grey-100 hover:bg-grey-200',
    transparent: 'text-grey-500 hover:underline',
  },
};

interface ButtonSignature {
  Element: HTMLButtonElement | HTMLAnchorElement;
  Args: {
    variant?: 'solid' | 'muted' | 'transparent';
    tone?: keyof typeof toneClasses;
    size?: 'standard' | 'large';
    disabled?: boolean;
    href?: string;
  };
  Blocks: {
    default: [];
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    Button: typeof Button;
  }
}

export default class Button extends Component<ButtonSignature> {
  get variant() {
    return this.args.variant ?? 'solid';
  }

  get tone() {
    return this.args.tone ?? 'accent';
  }

  get size() {
    return this.args.size ?? 'standard';
  }

  get classNames() {
    const { variant, tone, size } = this;
    const fontClass = variant === 'transparent' ? 'text-base' : 'text-md';
    let spacingClass = size === 'standard' ? 'px-6 py-3' : 'px-8 py-5';

    if (variant === 'transparent') {
      spacingClass = 'p-0';
    }

    const toneVariant = this.args.disabled
      ? [toneClasses['disabled'].common, toneClasses['disabled'][variant]]
      : [toneClasses[tone].common, toneClasses[tone][variant]];

    return [fontClass, spacingClass, ...toneVariant].join(' ');
  }
  <template>
    {{#let
      'flex items-center justify-center font-extrabold leading-snug rounded-md focus:outline-none ring-offset-1 focus-visible:ring-2'
      as |baseClasses|
    }}
      {{#if @href}}
        <a
          class='{{baseClasses}} {{this.classNames}}'
          href={{@href}}
          ...attributes
        >
          {{yield}}
        </a>
      {{else}}
        <button
          class='{{baseClasses}} {{this.classNames}}'
          type='button'
          disabled={{@disabled}}
          ...attributes
        >
          {{yield}}
        </button>
      {{/if}}
    {{/let}}
  </template>
}
